/**
 * `hour` → **Customize Hour column** — rebuilt 1:1 from the capture
 * (`discovery/ui-reference/editors/hour/dialog-settings.{png,json}`).
 *
 * The smallest dialog monday draws: a 320 × **125** card, the title *Hour column settings*, the
 * section label **Format** (45 × 21) and one 288 × 32 two-button group, *12H* | *24H*
 * (144 px each). Nothing else is in the capture and nothing else is in `hourSettingsSchema`.
 *
 * `settings.format` is *"`24H` | `12H`; if omitted, defaults to `12H`"* `[09§A.7 hour]`, and it
 * is **display only**: `toText` renders the 12-hour form on every cell whatever this says
 * (spec M2 tier-2 §5.11), so changing it repaints the grid and never touches a value.
 */
import type { HourSettings } from '@elizmonday/shared';
import { HideSummaryField, SegmentedField, SettingsForm, useSettingsDraft } from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

const FORMATS = [
  { id: '12H', label: '12H', value: '12H' },
  { id: '24H', label: '24H', value: '24H' },
] as const;

export function HourSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<HourSettings>) {
  const [draft, patch] = useSettingsDraft<HourSettings>(settings);
  return (
    <SettingsForm
      title="Hour column settings"
      testId="hour-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SegmentedField
        label="Format"
        testId="hour-format"
        value={draft.format ?? '12H'}
        options={FORMATS}
        onChange={(next) => patch({ format: next })}
      />
      <HideSummaryField
        testId="hour-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
